import React, { useEffect, useMemo, useState } from 'react';
import { resolveImageUrl } from '../../utils/imageUrl';
import type { BusinessStaffDto, BranchDto } from '../../types/api-responses';
import styles from './BranchRosterModal.module.css';

/**
 * CR-2026-05-16-v2.5 — Branch Roster modal.
 *
 * Secondary surface for staff↔branch (the primary is the chip section in
 * ManageStaffModal). Answers the "I'm opening Branch D and want to roster
 * 5 existing staff there" workflow in one trip instead of opening 5 staff
 * cards.
 *
 * Implicit-all rule (mirrors the BE):
 *   - Staff with empty branchIds is eligible at ALL active branches by
 *     default. The modal pre-selects them.
 *   - If the owner toggles an implicit-all staff OFF for this branch, we
 *     "materialize" their branchIds to (allBranches - thisBranch), making
 *     them explicit-elsewhere. This makes the simple click do the right
 *     thing without forcing the owner to think about defaults.
 *
 * Save flow: per-staff diff. For each staff whose membership changed we
 * call setStaffBranches with their new full list. Not atomic across
 * staff (one failure doesn't roll back the others) — surfaces per-row
 * error if any.
 */

export interface BranchRosterModalProps {
    isOpen: boolean;
    branch: BranchDto;
    allStaff: BusinessStaffDto[];
    allBranches: BranchDto[];
    onClose: () => void;
    /** Called after a successful save so the parent can refetch. */
    onSaved: () => void;
    setStaffBranches: (staffId: number, branchIds: number[]) => Promise<boolean>;
}

/** Initials fallback for staff without a photo. */
function getInitials(fullName: string): string {
    const trimmed = (fullName ?? '').trim();
    if (!trimmed) return '?';
    return trimmed
        .split(/\s+/)
        .map(p => p[0] || '')
        .join('')
        .slice(0, 2)
        .toUpperCase();
}

function resolveAvatar(url: string | null | undefined): string | null {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return resolveImageUrl(url);
}

const BranchRosterModal: React.FC<BranchRosterModalProps> = ({
    isOpen,
    branch,
    allStaff,
    allBranches,
    onClose,
    onSaved,
    setStaffBranches,
}) => {
    // selectedSet = which staffIds are "in" this branch's roster after edits
    const [selectedSet, setSelectedSet] = useState<Set<number>>(new Set());
    // originalSet = initial selection (for diff at save time)
    const [originalSet, setOriginalSet] = useState<Set<number>>(new Set());
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [perStaffErrors, setPerStaffErrors] = useState<Record<number, string>>({});

    // Pre-fill selection: a staff is "in" this branch if their branchIds is
    // empty (implicit-all) OR if their branchIds includes this branch.
    useEffect(() => {
        if (!isOpen) return;
        const initial = new Set<number>();
        for (const s of allStaff) {
            const ids = s.branchIds ?? [];
            const isIn = ids.length === 0 || ids.includes(branch.id);
            if (isIn) initial.add(s.id);
        }
        setSelectedSet(new Set(initial));
        setOriginalSet(new Set(initial));
        setSearch('');
        setPerStaffErrors({});
    }, [isOpen, branch.id, allStaff]);

    const activeStaff = useMemo(() => allStaff.filter(s => s.isActive), [allStaff]);

    const visibleStaff = useMemo(() => {
        const q = search.trim().toLowerCase();
        const filtered = q
            ? activeStaff.filter(s =>
                  (s.fullName ?? '').toLowerCase().includes(q) ||
                  (s.position ?? '').toLowerCase().includes(q) ||
                  (s.email ?? '').toLowerCase().includes(q),
              )
            : activeStaff;
        // Sort: selected first, then alphabetical
        return [...filtered].sort((a, b) => {
            const aSel = selectedSet.has(a.id) ? 0 : 1;
            const bSel = selectedSet.has(b.id) ? 0 : 1;
            if (aSel !== bSel) return aSel - bSel;
            return (a.fullName ?? '').localeCompare(b.fullName ?? '');
        });
    }, [activeStaff, search, selectedSet]);

    const allBranchIds = useMemo(
        () => allBranches.filter(b => b.active).map(b => b.id),
        [allBranches],
    );

    const toggleStaff = (id: number) => {
        if (saving) return;
        setSelectedSet(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    /** Diff: list of staff whose membership in the branch's roster changed. */
    const changes = useMemo(() => {
        const added: number[] = [];
        const removed: number[] = [];
        Array.from(selectedSet).forEach(id => {
            if (!originalSet.has(id)) added.push(id);
        });
        Array.from(originalSet).forEach(id => {
            if (!selectedSet.has(id)) removed.push(id);
        });
        return { added, removed };
    }, [selectedSet, originalSet]);

    const hasChanges = changes.added.length + changes.removed.length > 0;

    const handleSave = async () => {
        if (saving || !hasChanges) return;
        setSaving(true);
        setPerStaffErrors({});

        const errors: Record<number, string> = {};

        const staffById = new Map(allStaff.map(s => [s.id, s]));

        // ADDED: this branch was added to their list.
        for (const staffId of changes.added) {
            const s = staffById.get(staffId);
            if (!s) continue;
            const existing = s.branchIds ?? [];
            // If they had empty (implicit-all), an "add" is a no-op write since
            // they were already eligible everywhere — skip the PUT.
            if (existing.length === 0) continue;
            const next = Array.from(new Set([...existing, branch.id]));
            try {
                await setStaffBranches(staffId, next);
            } catch (e) {
                errors[staffId] = e instanceof Error ? e.message : 'Failed to add';
            }
        }

        // REMOVED: this branch should no longer be in their list.
        for (const staffId of changes.removed) {
            const s = staffById.get(staffId);
            if (!s) continue;
            const existing = s.branchIds ?? [];
            let next: number[];
            if (existing.length === 0) {
                // Materialize: they were implicit-all, switch to explicit
                // "all branches except this one" so the removal sticks.
                next = allBranchIds.filter(id => id !== branch.id);
            } else {
                next = existing.filter(id => id !== branch.id);
            }
            try {
                await setStaffBranches(staffId, next);
            } catch (e) {
                errors[staffId] = e instanceof Error ? e.message : 'Failed to remove';
            }
        }

        setSaving(false);
        setPerStaffErrors(errors);

        if (Object.keys(errors).length === 0) {
            onSaved();
            onClose();
        }
        // else: keep modal open so the owner can see per-row errors
    };

    if (!isOpen) return null;

    const selectedCount = selectedSet.size;
    const totalCount = activeStaff.length;

    return (
        <div className={`${styles.modalBackground} ${styles.open}`}>
            <div className={`${styles.modalContainer} ${styles.open}`}>
                {saving && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.spinner} />
                    </div>
                )}

                <button
                    type="button"
                    className={styles.closeButton}
                    onClick={() => !saving && onClose()}
                    aria-label="Close"
                    disabled={saving}
                >
                    ×
                </button>

                <div className={styles.body}>
                    <div>
                        <h2 className={styles.header}>Roster — {branch.name}</h2>
                        <p className={styles.subheader}>
                            Pick which staff work at this branch. Staff marked <strong>AUTO</strong> are
                            currently eligible at all branches by default — clicking them off will narrow
                            them to "everywhere except this branch."
                        </p>
                    </div>

                    <div className={styles.summaryRow}>
                        <span className={styles.summaryCount}>
                            {selectedCount} of {totalCount} active staff
                        </span>
                        {hasChanges && (
                            <span>
                                {changes.added.length > 0 && `+${changes.added.length} added`}
                                {changes.added.length > 0 && changes.removed.length > 0 && ' · '}
                                {changes.removed.length > 0 && `-${changes.removed.length} removed`}
                            </span>
                        )}
                    </div>

                    {activeStaff.length === 0 ? (
                        <div className={styles.emptyState}>
                            No active staff in your business yet. Add staff in <strong>Manage Staff</strong>,
                            then come back here to roster them across branches.
                        </div>
                    ) : (
                        <>
                            <input
                                type="text"
                                className={styles.searchInput}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder={`Search ${totalCount} staff…`}
                                aria-label="Search staff"
                                disabled={saving}
                            />

                            <ul className={styles.staffList}>
                                {visibleStaff.map(s => {
                                    const ids = s.branchIds ?? [];
                                    const isImplicit = ids.length === 0;
                                    const isSelected = selectedSet.has(s.id);
                                    const photoSrc = resolveAvatar(s.photoUrl);
                                    const err = perStaffErrors[s.id];
                                    return (
                                        <li key={s.id}>
                                            <button
                                                type="button"
                                                className={`${styles.staffRow} ${isSelected ? styles.staffRowSelected : ''} ${isImplicit && isSelected ? styles.staffRowImplicit : ''}`}
                                                onClick={() => toggleStaff(s.id)}
                                                disabled={saving}
                                                aria-pressed={isSelected}
                                            >
                                                <div className={styles.avatar} aria-hidden="true">
                                                    {photoSrc ? (
                                                        <img src={photoSrc} alt="" />
                                                    ) : (
                                                        getInitials(s.fullName)
                                                    )}
                                                </div>
                                                <div className={styles.staffMain}>
                                                    <div className={styles.staffName}>
                                                        {s.fullName}
                                                        {isImplicit && isSelected && (
                                                            <span className={`${styles.tagPill} ${styles.tagAuto}`}>
                                                                Auto
                                                            </span>
                                                        )}
                                                        {s.position && (
                                                            <span className={`${styles.tagPill} ${styles.tagPosition}`}>
                                                                {s.position}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {s.email && (
                                                        <div className={styles.staffMeta}>{s.email}</div>
                                                    )}
                                                    {err && (
                                                        <div className={styles.staffError}>⚠ {err}</div>
                                                    )}
                                                </div>
                                                <div className={styles.checkBadge} aria-hidden="true">
                                                    {isSelected ? '✓' : ''}
                                                </div>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    )}

                    <p className={styles.summaryHint}>
                        <strong>Tip:</strong> the canonical surface for staff↔branch is the <strong>Manage Staff</strong> modal —
                        this view is for bulk roster moves like opening a new branch.
                    </p>
                </div>

                <div className={styles.footer}>
                    <button
                        type="button"
                        className={`${styles.btn} ${styles.btnGhost}`}
                        onClick={() => !saving && onClose()}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={`${styles.btn} ${styles.btnPrimary}`}
                        onClick={handleSave}
                        disabled={saving || !hasChanges}
                    >
                        {saving
                            ? 'Saving…'
                            : hasChanges
                                ? `Save changes`
                                : 'No changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BranchRosterModal;
